import React, { useEffect, useState } from "react"
import { useLang } from "hooks/useLang"

const EXIT_DURATION = 450

/* ------------------------------
   BASE SKELETON BLOCK
------------------------------ */
const Skeleton = ({ className = "" }: { className?: string }) => (
  <div
    className={`relative overflow-hidden rounded-md 
      bg-gray-200/80 dark:bg-gray-700/70 
      animate-skeletonPulse ${className}`}
  >
    <span className="absolute inset-0 -translate-x-full animate-[shimmer_1.4s_infinite] bg-gradient-to-r from-transparent via-white/40 to-transparent" />
  </div>
)

/* ------------------------------
   EVENT CARD SKELETON
------------------------------ */
const EventCardSkeleton = ({
  aspect = "widescreen",
}: {
  aspect?: "widescreen" | "portrait"
}) => (
  <div className="flex flex-col gap-3 animate-floatSlow">
    <Skeleton
      className={`w-full rounded-xl ${
        aspect === "portrait" ? "aspect-[9/16]" : "aspect-[16/9]"
      }`}
    />
    <Skeleton className="h-4 w-[80%]" />
    <Skeleton className="h-3 w-[55%]" />
  </div>
)

/* ------------------------------
   SECTION SKELETON
------------------------------ */
const SectionSkeleton = ({
  index,
  layout = "slider",
  cards = 4,
}: {
  index: number
  layout?: "slider" | "grid"
  cards?: number
}) => (
  <section
    className="my-14 flex flex-col gap-5 opacity-0 animate-sectionIn"
    style={{ animationDelay: `${index * 120}ms` }}
  >
    {/* Header */}
    <div className="layout lg:max-w-[1230px] flex items-end justify-between">
      <div className="space-y-2">
        <Skeleton className="h-5 w-[180px]" />
        <Skeleton className="h-3 w-[260px]" />
      </div>
      <div className="hidden md:flex gap-2">
        <Skeleton className="h-10 w-10 rounded-full" />
        <Skeleton className="h-10 w-10 rounded-full" />
      </div>
    </div>

    {/* Content */}
    {layout === "slider" ? (
      <div className="layout md:max-w-[1230px] flex gap-6 overflow-hidden">
        {Array.from({ length: cards }).map((_, i) => (
          <div key={i} className="min-w-[260px]">
            <EventCardSkeleton />
          </div>
        ))}
      </div>
    ) : (
      <div className="layout md:max-w-[1230px] grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {Array.from({ length: cards }).map((_, i) => (
          <EventCardSkeleton key={i} />
        ))}
      </div>
    )}
  </section>
)

/* ------------------------------
   MAIN HOME SKELETON
------------------------------ */
const HomePageSkeleton = ({ active }: { active: boolean }) => {
  const { isAr } = useLang()
  const [mounted, setMounted] = useState(active)
  const [exiting, setExiting] = useState(false)

  useEffect(() => {
    if (active) {
      setMounted(true)
      setExiting(false)
    } else if (mounted) {
      setExiting(true)
      const t = setTimeout(() => setMounted(false), EXIT_DURATION)
      return () => clearTimeout(t)
    }
  }, [active])

  if (!mounted) return null

  return (
    <div
      dir={isAr ? "rtl" : "ltr"}
      className={`
        fixed inset-0 z-[40] overflow-y-auto
        bg-white dark:bg-black
        ${exiting ? "animate-skeletonOut" : "animate-fadeIn"}
      `}
    >
      {/* HERO */}
      <div className="layout md:max-w-[1220px] w-full mt-[90px]">
        <Skeleton className="w-full aspect-[16/8] rounded-xl animate-floatSlow" />

        {/* BLUE CTA */}
        <div className="mt-[-18px] h-[100px] rounded-xl bg-[#49B4FF]/30 flex items-center px-6 gap-4">
          <Skeleton className="h-[50px] w-[50px] rounded-full" />
          <Skeleton className="h-4 w-[200px]" />
          <div className="ml-auto">
            <Skeleton className="h-9 w-[120px] rounded-full" />
          </div>
        </div>
      </div>

      {/* SECTIONS */}
      <SectionSkeleton index={1} layout="slider" cards={4} />
      <SectionSkeleton index={2} layout="grid" cards={8} />
      <SectionSkeleton index={3} layout="slider" cards={6} />
      <SectionSkeleton index={4} layout="grid" cards={4} />
    </div>
  )
}

export default HomePageSkeleton
